import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import type { UIMessage } from 'ai';

import {
  COMPANION_STARTERS,
  COMPANION_THREAD,
  COMPANION_THREADS,
} from 'querykeys';

import { useToasts } from 'features/toaster/useToasts';

import messages from './messages';
import type { CompanionWorkspace, ThreadMeta } from '../types';
import {
  deleteThread,
  getStarters,
  getThreadMessages,
  getThreads,
  renameThread,
} from '../utils/companionEndpoints';

type OpenChat = { id: string; initialMessages: UIMessage[] };

const newChatState = (): OpenChat => ({
  id: crypto.randomUUID(),
  initialMessages: [],
});

export const useCompanionWorkspace = (): CompanionWorkspace => {
  const { formatMessage } = useIntl();
  const { showErrorToast } = useToasts();
  const queryClient = useQueryClient();

  const [chat, setChat] = useState<OpenChat>(newChatState);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  // the async callbacks below outlive the render that created them
  const chatIdRef = useRef(chat.id);
  chatIdRef.current = chat.id;
  const latestOpenRef = useRef<ThreadMeta['id']>();

  const threadsQuery = useQuery<ThreadMeta[]>(COMPANION_THREADS, getThreads);
  const startersQuery = useQuery(COMPANION_STARTERS, getStarters);

  const refreshThreads = () => queryClient.invalidateQueries(COMPANION_THREADS);

  const rename = useMutation(
    ({ threadId, name }: { threadId: ThreadMeta['id']; name: string }) =>
      renameThread(threadId, name),
    {
      onSuccess: refreshThreads,
      onError: () => showErrorToast(formatMessage(messages.threadRenameFailed)),
    }
  );

  const remove = useMutation(
    (threadId: ThreadMeta['id']) => deleteThread(threadId),
    {
      onSuccess: refreshThreads,
      onError: () => showErrorToast(formatMessage(messages.threadDeleteFailed)),
    }
  );

  const startNewChat = () => {
    setChat(newChatState());
    setIsHistoryOpen(false);
  };

  const openThread = async (threadId: ThreadMeta['id']) => {
    latestOpenRef.current = threadId;
    try {
      const initialMessages = await queryClient.fetchQuery(
        [COMPANION_THREAD, threadId],
        () => getThreadMessages(threadId)
      );
      // a slower load for an earlier click must not replace the newer selection
      if (latestOpenRef.current !== threadId) return;
      setChat({ id: threadId, initialMessages });
      setIsHistoryOpen(false);
    } catch {
      showErrorToast(formatMessage(messages.threadLoadFailed));
    }
  };

  return {
    chatId: chat.id,
    initialMessages: chat.initialMessages,
    isHistoryOpen,
    openThread,
    refreshThreads,
    renameThread: (threadId, name) => rename.mutate({ threadId, name }),
    deleteThread: threadId =>
      remove.mutate(threadId, {
        onSuccess: () => {
          if (threadId === chatIdRef.current) startNewChat();
        },
      }),
    starters: startersQuery.data ?? [],
    startNewChat,
    threads: threadsQuery.data ?? [],
    threadsError: threadsQuery.isError,
    threadsLoading: threadsQuery.isLoading,
    toggleHistory: () => setIsHistoryOpen(current => !current),
  };
};
